import React, { useState } from 'react';
import { AdminPanel } from '../components/AdminPanel';
import { ArrowLeft, Lock, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../lib/utils';

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState('');
  const [waitlistData, setWaitlistData] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [sourceStats, setSourceStats] = useState([]);
  const [autoApproveNewSignups, setAutoApproveNewSignups] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = async (key) => {
    setIsLoading(true);
    setError(null);
    try {
      const API_URL = getApiUrl();
      const headers = { 'X-Admin-Key': key };

      const [waitlistRes, feedbackRes, settingsRes, sourceStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/waitlist`, { headers }),
        fetch(`${API_URL}/api/rainbow-room-feedback`, { headers }),
        fetch(`${API_URL}/api/admin/settings`, { headers }),
        fetch(`${API_URL}/api/admin/source-stats`, { headers })
      ]);

      if (waitlistRes.status === 401 || feedbackRes.status === 401) {
        setIsUnlocked(false);
        setAdminKey('');
        setAuthError('Invalid admin key. Please try again.');
        return;
      }
      if (!waitlistRes.ok) {
        throw new Error('Failed to fetch waitlist data');
      }
      if (!feedbackRes.ok) {
        throw new Error('Failed to fetch Rainbow Room feedback data');
      }

      const waitlist = await waitlistRes.json();
      const feedback = await feedbackRes.json();
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setAutoApproveNewSignups(!!settings.autoApproveNewSignups);
      }
      if (sourceStatsRes.ok) {
        const stats = await sourceStatsRes.json();
        setSourceStats(stats.bySource || []);
      }

      setWaitlistData(waitlist.map(entry => ({
        id: entry.id,
        name: entry.name,
        email: entry.email,
        phone: entry.phone || '',
        note: entry.note || '',
        foundingGuardian: entry.founding_guardian || false,
        approved: entry.approved || false,
        source: entry.source || 'direct',
        campaign: entry.campaign || '',
        createdAt: entry.created_at
      })));

      setFeedbackData(feedback.map(entry => ({
        id: entry.id,
        name: entry.name,
        email: entry.email,
        petName: entry.petName || '',
        walkGarden: entry.walkGarden || '',
        peaceful: entry.peaceful,
        fixFirst: entry.fixFirst || '',
        createdAt: entry.created_at
      })));
    } catch (err) {
      setError(err.message);
      setWaitlistData([]);
      setFeedbackData([]);
      setSourceStats([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;

    setAuthError('');
    setIsAuthenticating(true);
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/waitlist`, {
        headers: { 'X-Admin-Key': keyInput }
      });

      if (res.status === 401) {
        setAuthError('Invalid admin key. Please try again.');
        return;
      }
      if (!res.ok) {
        setAuthError('Could not connect. Please try again.');
        return;
      }

      setAdminKey(keyInput);
      setIsUnlocked(true);
      loadData(keyInput);
    } catch (err) {
      setAuthError('Could not connect. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl p-8 bg-slate-900/60 border border-slate-700/50 shadow-2xl text-center" data-testid="admin-locked-card">
          <div className="w-14 h-14 rounded-full bg-slate-800/70 border border-slate-700 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-slate-400" />
          </div>
          <h1 className="text-lg font-bold text-white mb-1">Admin Access Locked</h1>
          <p className="text-sm text-slate-400 mb-6">Enter the admin key to view Guardian data.</p>

          <form onSubmit={handleUnlock} className="space-y-3">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              autoComplete="off"
              data-testid="admin-key-input"
              className="w-full px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
              disabled={isAuthenticating}
            />

            {authError && (
              <div className="flex items-center gap-2 text-xs text-rose-400" data-testid="admin-key-error">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={isAuthenticating || !keyInput.trim()}
              data-testid="admin-unlock-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-medium text-slate-900 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAuthenticating ? 'Checking...' : 'Unlock'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Landing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 text-white hover:bg-slate-800 hover:border-slate-600 transition-all duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Landing
        </button>
      </div>

      <AdminPanel 
        waitlistData={waitlistData}
        feedbackData={feedbackData}
        sourceStats={sourceStats}
        adminKey={adminKey}
        onRefresh={() => loadData(adminKey)}
        onDeleteWaitlistEntry={async (id) => {
          const API_URL = getApiUrl();
          await fetch(`${API_URL}/api/waitlist/${id}`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
          loadData(adminKey);
        }}
        onDeleteFeedbackEntry={async (id) => {
          const API_URL = getApiUrl();
          await fetch(`${API_URL}/api/rainbow-room-feedback/${id}`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
          loadData(adminKey);
        }}
        onApproveWaitlistEntry={async (id) => {
          const API_URL = getApiUrl();
          await fetch(`${API_URL}/api/waitlist/${id}/approve`, { method: 'POST', headers: { 'X-Admin-Key': adminKey } });
          loadData(adminKey);
        }}
        onMakeFoundingGuardian={async (id) => {
          const API_URL = getApiUrl();
          await fetch(`${API_URL}/api/waitlist/${id}/make-founding-guardian`, { method: 'POST', headers: { 'X-Admin-Key': adminKey } });
          loadData(adminKey);
        }}
        autoApproveNewSignups={autoApproveNewSignups}
        onToggleAutoApprove={async (value) => {
          const API_URL = getApiUrl();
          setAutoApproveNewSignups(value);
          await fetch(`${API_URL}/api/admin/settings`, {
            method: 'POST',
            headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
            body: JSON.stringify({ autoApproveNewSignups: value })
          });
        }}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
};

export default AdminPage;
